import type { Line, Stub, Totals, Trend, Run } from './types';

export function getToken(): string {
  return localStorage.getItem('access_token') || localStorage.getItem('token') || '';
}

export function authHeaders(): Record<string, string> {
  return {
    Authorization: 'Bearer ' + getToken(),
    'Content-Type': 'application/json',
  };
}

export function fmtMoney(n: number | string | null | undefined): string {
  const v = Number(n) || 0;
  return '$' + v.toLocaleString('en-CA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const parts = String(iso).split('-');
  if (parts.length < 3) return String(iso);
  return parts[2] + '/' + parts[1] + '/' + parts[0];
}

export function fmtDateFull(iso: string | null | undefined): string {
  if (!iso) return '';
  const dt = new Date(iso + 'T00:00:00');
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return days[dt.getDay()] + ', ' + fmtDate(iso);
}

export function scheduleFrequencyLabel(freq: string | undefined | null): string {
  if (!freq) return '';
  const map: Record<string, string> = {
    weekly: 'Weekly',
    biweekly: 'Biweekly',
    semimonthly: 'Semi-monthly',
    monthly: 'Monthly',
  };
  return map[freq] || freq;
}

export function mapStubToLine(stub: Stub): Line {
  const hours =
    Number(stub.hours_regular || 0) +
    Number(stub.hours_overtime || 0) +
    Number(stub.hours_stat_holiday || 0) +
    Number(stub.hours_vacation || 0) +
    Number(stub.hours_sick || 0);
  const gross = Number(stub.gross_pay || 0);
  const rate = hours > 0 && gross > 0 ? Number((gross / hours).toFixed(2)) : 0;

  return {
    employee_id: stub.employee_id,
    stub_id: stub.id,
    name: stub.employee_name || '',
    classification: stub.classification || 'Hourly',
    hourly_rate: rate,
    payment_method: stub.payment_method || 'cheque',
    total_hours: hours,
    gross_pay: gross,
    employee_taxes: Number(stub.total_employee_deductions || 0),
    net_pay: Number(stub.net_pay || 0),
    employer_taxes: Number(stub.total_employer_contributions || 0),
    change_in_gross_pct: stub.change_in_gross_pct != null ? Number(stub.change_in_gross_pct) : null,
    includes_stat: Number(stub.hours_stat_holiday || 0) > 0,
  };
}

export function computeTotals(lines: Line[]): Totals {
  const gross = lines.reduce((s, l) => s + l.gross_pay, 0);
  const employee_taxes = lines.reduce((s, l) => s + l.employee_taxes, 0);
  const net = lines.reduce((s, l) => s + l.net_pay, 0);
  const employer_taxes = lines.reduce((s, l) => s + l.employer_taxes, 0);
  const total_hours = lines.reduce((s, l) => s + l.total_hours, 0);
  const cra_remittance = employee_taxes + employer_taxes;
  const total_cost = net + cra_remittance;
  const take_home_pct = total_cost > 0 ? Math.round((net / total_cost) * 1000) / 10 : 0;
  const remit_pct = total_cost > 0 ? Math.round((cra_remittance / total_cost) * 1000) / 10 : 0;
  return { gross, employee_taxes, net, employer_taxes, total_hours, cra_remittance, total_cost, take_home_pct, remit_pct };
}

export function computeTrend(priorRuns: Run[], currentTotalCost: number): Trend | null {
  if (priorRuns.length === 0) return null;
  const lastRun = [...priorRuns].sort(
    (a, b) => new Date(b.pay_date || 0).getTime() - new Date(a.pay_date || 0).getTime(),
  )[0];
  const lastTotal = Number(lastRun.total_payroll_cost || lastRun.total_cost || 0);
  if (lastTotal === 0) return null;
  const pct = ((currentTotalCost - lastTotal) / lastTotal) * 100;
  return {
    pct,
    dir: pct >= 0 ? 'up' : 'down',
    abs: Math.abs(Math.round(pct)),
  };
}